import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram, Mail, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { useContent } from "../lib/content";
import { DISCLAIMER } from "../data/services";

export const Footer = () => {
  const { content } = useContent();
  const phoneHref = `tel:+1${content.contact.phoneDisplay.replace(/\D/g, "")}`;

  return (
    <footer className="bg-forest text-cream" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 py-20 md:grid-cols-2 lg:grid-cols-4 lg:px-10">
        <div>
          <Logo size={96} />
          <p className="mt-6 max-w-xs font-display text-xl italic leading-snug text-cream/80">
            {content.footer.tagline}
          </p>
        </div>

        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Quick Links</h3>
          <ul className="mt-6 space-y-3">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About Us" },
              { to: "/services", label: "Services" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="link-underline text-sm text-cream/75 transition-colors duration-300 hover:text-cream"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Contact Info</h3>
          <ul className="mt-6 space-y-4 text-sm text-cream/75">
            <li>
              <a href={phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors duration-300 hover:text-cream">
                <Phone className="h-4 w-4 text-gold" strokeWidth={1.5} />
                {content.contact.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${content.contact.email}`} data-testid="footer-email-link" className="flex items-center gap-3 break-all transition-colors duration-300 hover:text-cream">
                <Mail className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                {content.contact.email}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Let's Connect</h3>
          <p className="mt-6 text-sm leading-relaxed text-cream/75">
            Have a question or ready to get started? We're here to help.
          </p>
          <Link
            to="/contact"
            data-testid="footer-message-button"
            className="group mt-6 inline-flex items-center gap-2 rounded-full border border-gold/60 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
          >
            Send Us a Message
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61591880525468"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Rooted Business Advisors on Facebook"
              data-testid="footer-facebook-link"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/25 text-cream/75 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-forest"
            >
              <Facebook className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a
              href="https://www.instagram.com/rooted_business_advisors/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Rooted Business Advisors on Instagram"
              data-testid="footer-instagram-link"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/25 text-cream/75 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-forest"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="mx-auto max-w-7xl px-6 py-6 lg:px-10">
          <p className="text-[11px] leading-relaxed text-cream/45" data-testid="footer-disclaimer">
            {DISCLAIMER}
          </p>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-cream/50 md:flex-row lg:px-10">
          <span>© {new Date().getFullYear()} Rooted Business Advisors, LLC. All rights reserved.</span>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2" aria-label="Legal">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors duration-300 hover:text-cream">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors duration-300 hover:text-cream">
              Terms of Use
            </Link>
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="transition-colors duration-300 hover:text-cream">
              Professional Disclaimer
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};
